import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { getTermBySlug, categories } from '../data/glossary';
import './GlossaryTerm.css';

export default function GlossaryTerm() {
  const { slug } = useParams();
  const term = getTermBySlug(slug);

  if (!term) {
    return (
      <div className="gt-not-found">
        <h2>Term not found</h2>
        <p>We couldn't find a glossary entry for "{slug}".</p>
        <Link to="/glossary" className="gt-back-link">← Back to glossary</Link>
      </div>
    );
  }

  const cat = categories.find(c => c.slug === term.category);
  const related = (term.relatedTerms || [])
    .map(s => getTermBySlug(s))
    .filter(Boolean);

  const formattedDate = term.lastUpdated
    ? new Date(term.lastUpdated).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })
    : null;

  return (
    <div className="gt-page">
      <Helmet>
        <title>{term.title} — DhanAcademy</title>
        <meta name="description" content={term.metaDescription || term.quickDefinition} />
        <meta property="og:title" content={`${term.title} — DhanAcademy`} />
        <meta property="og:description" content={term.metaDescription || term.quickDefinition} />
        <meta property="og:type" content="article" />
      </Helmet>

      {/* Breadcrumbs */}
      <div className="gt-breadcrumb-bar">
        <div className="gt-breadcrumb-inner">
          <Link to="/" className="gt-crumb">Home</Link>
          <span className="gt-crumb-sep">›</span>
          <Link to="/glossary" className="gt-crumb">Glossary</Link>
          {cat && (
            <>
              <span className="gt-crumb-sep">›</span>
              <Link to="/glossary" className="gt-crumb">{cat.name}</Link>
            </>
          )}
          <span className="gt-crumb-sep">›</span>
          <span className="gt-crumb gt-crumb-current">{term.title}</span>
        </div>
      </div>

      <div className="gt-content">

        {/* Title block */}
        <div className="gt-header">
          <h1 className="gt-full-title">{term.fullTitle || term.title}</h1>
          {term.fullTitle && term.fullTitle !== term.title && (
            <div className="gt-short-title">{term.title}</div>
          )}
          {cat && (
            <span className="gt-category-pill" style={{ background: cat.color + '18', color: cat.color }}>
              {cat.name}
            </span>
          )}
        </div>

        {/* Quick definition */}
        <div className="gt-quick-box">
          <div className="gt-quick-label">QUICK DEFINITION</div>
          <div className="gt-quick-text">{term.quickDefinition}</div>
        </div>

        {/* Plain English */}
        {term.plainEnglish && (
          <div className="gt-section">
            <h2 className="gt-section-heading">Plain English</h2>
            <p className="gt-prose">{term.plainEnglish}</p>
          </div>
        )}

        {/* Formula */}
        {term.formula && (
          <div className="gt-section">
            <h2 className="gt-section-heading">Formula</h2>
            <div className="gt-formula-box">
              <div className="gt-formula-label">FORMULA</div>
              <div className="gt-formula">{term.formula}</div>
            </div>
          </div>
        )}

        {/* Indian Example */}
        {term.indianExample && (
          <div className="gt-section">
            <h2 className="gt-section-heading">Indian Example</h2>
            <p className="gt-prose">{term.indianExample}</p>
          </div>
        )}

        {/* What's a good number */}
        {term.whatsGood && (
          <div className="gt-section">
            <h2 className="gt-section-heading">What's a good number</h2>
            <p className="gt-prose">{term.whatsGood}</p>
          </div>
        )}

        {/* Common Mistake */}
        {term.commonMistake && (
          <div className="gt-section">
            <h2 className="gt-section-heading">Common Mistake</h2>
            <div className="gt-mistake-box">
              <div className="gt-mistake-label">
                <span className="gt-mistake-icon">⚠</span>
                COMMON MISTAKE
              </div>
              <p className="gt-mistake-text">{term.commonMistake}</p>
            </div>
          </div>
        )}

        {/* Related Terms */}
        {related.length > 0 && (
          <div className="gt-section">
            <h2 className="gt-section-heading">Related Terms</h2>
            <div className="gt-related">
              {related.map(r => (
                <Link key={r.slug} to={`/glossary/${r.slug}`} className="gt-related-pill">
                  {r.title}
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* Last updated */}
        {formattedDate && (
          <div className="gt-updated">Last updated: {formattedDate}</div>
        )}

        {/* Disclaimer */}
        <div className="gt-disclaimer">
          <div className="gt-disclaimer-icon">ⓘ</div>
          <p className="gt-disclaimer-text">
            This content is for educational purposes only. DhanAcademy is not a SEBI-registered investment advisor.
            Information here should not be considered financial advice. Please consult a qualified advisor before
            making investment decisions.
          </p>
        </div>

      </div>
    </div>
  );
}
